// Playable Wolf — MONEY model, matched to GolfGamesScorecard's utils/betting.ts
// (calcRunningBets). Running dollars + a win-loss record (as the Wolf). No 3/2/1
// points — the app doesn't use them on the scorecard. Self-contained (strokes
// via ggHandicap sibling; no live imports).
//
// Rotation (matches betting.ts, 0-based hole h):
//   standard / Biggest Loser: wolfIdx = h % n        (forward)
//   Biggest Loser Shuck (BLS): wolfIdx = (n - h%n) % n (backward), + a Shuck =
//     the player before the Wolf: shuckIdx = (wolfIdx - 1 + n) % n.
//   (Biggest Loser is money-identical to standard; its "loser tees first" is a
//    play-order convention with no money effect — matching the app.)
//
// Money (betVal = (override ?? baseBet+carried) × doubleMult × howl × blind):
//   Lone Wolf, unique low: wolf wins → +betVal from each other; loses → −betVal
//     to each. Non-unique low → push. Partnered/Shuck: team best ball vs field
//     best ball. split-pot (default): pot = betVal × #losers ÷ #winners; each
//     loser −betVal. per-winner: each winner +betVal×#losers, loser −betVal×#winners.
//   A push carries the base bet forward (never doubles), same as the app.
//   Press = per-hole `overrides` (replace base, applied from a hole forward).
//   Double-this-hole = per-hole `multipliers` (×2/×3).
//
// REPAIRS vs the iOS app (built correct here; log to fix iOS later):
//   • Settlement uses the actual wolf picks (the app's results screen drops them
//     and mis-settles as lowest-score) — here settle is always Wolf-correct.
//   • Blind Wolf and The Howl are FUNCTIONAL here (label-only in the app):
//       Howl  = ×2 the hole's stake (wolf's option, any hole).
//       Blind = ×2 the stake on a declared blind LONE wolf (lone only).
//       They stack multiplicatively (×4 if both). Flagged for confirmation.
import { strokesPerHoleForMode } from './ggHandicap.js'

// Per-hole stroke grid for a player under the wolf stroke-allocation mode
// (default 'lowest' = off-the-lowest, matching the app's Wolf default).
export function wolfStrokeGrid(players, strokeMode = 'lowest', strokeIndex) {
  const field = players.map((p) => ({ playingHandicap: p.playingHandicap || 0 }))
  return players.map((_, pi) => strokesPerHoleForMode(strokeMode, field[pi], field, strokeIndex))
}

export function wolfIndexFor(hole, n, isBls = false) {
  const h = hole - 1 // 0-based, as betting.ts
  return isBls ? (((n - (h % n)) % n) + n) % n : ((h % n) + n) % n
}
export function shuckIndexFor(hole, n, isBls = false) {
  return (wolfIndexFor(hole, n, isBls) - 1 + n) % n
}

export function settleWolf(players, scoresByHole, picks, opts = {}) {
  const { baseBet = 0, wolfPayout = 'split-pot', isBls = false, net = false, strokeMode = 'lowest', strokeIndex, overrides = {}, multipliers = {} } = opts
  const n = (players || []).length
  const ids = players.map((p) => p.id)
  const nets = {}
  const wl = {}
  ids.forEach((id) => { nets[id] = 0; wl[id] = { w: 0, l: 0 } })
  const holes = []
  let carried = 0
  let stopped = false

  const grids = net ? wolfStrokeGrid(players, strokeMode, strokeIndex) : null
  const scoreOf = (pi, hole) => {
    const g = Number(scoresByHole?.[hole]?.[ids[pi]])
    if (!Number.isFinite(g) || g <= 0) return null
    const strokes = grids ? (grids[pi][hole - 1] || 0) : 0
    return g - strokes
  }

  for (let hole = 1; hole <= 18; hole++) {
    const wolfIdx = wolfIndexFor(hole, n, isBls)
    const shuckIdx = shuckIndexFor(hole, n, isBls)
    const wolfId = ids[wolfIdx]
    const pick = picks?.[hole] || (isBls
      ? { loneWolf: false, partnerId: null, shuckPartnerId: ids[shuckIdx] }
      : { loneWolf: true, partnerId: null })
    const partnerIdx = pick.partnerId != null ? ids.indexOf(pick.partnerId) : -1
    const shuckPickIdx = pick.shuckPartnerId != null ? ids.indexOf(pick.shuckPartnerId) : -1
    const isLone = !!pick.loneWolf && partnerIdx < 0 && shuckPickIdx < 0

    const baseForHole = overrides[hole] !== undefined ? Number(overrides[hole]) : baseBet + carried
    const betVal = baseForHole * (Number(multipliers[hole]) || 1) * (pick.howl ? 2 : 1) * (pick.blind && isLone ? 2 : 1)

    const vals = ids.map((_, pi) => scoreOf(pi, hole))
    const played = !stopped && vals.every((v) => Number.isFinite(v))

    let exchanged = false
    let winnerSide = null
    let teamIds = []

    if (played) {
      if (isLone) {
        teamIds = [wolfId]
        const min = Math.min(...vals)
        const holders = vals.map((s, i) => (s === min ? i : -1)).filter((i) => i >= 0)
        if (holders.length === 1) {
          const w = holders[0]
          const sign = w === wolfIdx ? +1 : -1
          for (let i = 0; i < n; i++) { if (i === wolfIdx) continue; nets[wolfId] += sign * betVal; nets[ids[i]] -= sign * betVal }
          winnerSide = w === wolfIdx ? 'wolf' : 'field'
          exchanged = true
        }
      } else {
        const team = [wolfIdx]
        if (partnerIdx >= 0) team.push(partnerIdx)
        if (shuckPickIdx >= 0 && shuckPickIdx !== partnerIdx) team.push(shuckPickIdx)
        const teamSet = new Set(team)
        const others = []
        for (let i = 0; i < n; i++) if (!teamSet.has(i)) others.push(i)
        teamIds = team.map((i) => ids[i])
        const wolfBest = Math.min(...team.map((i) => vals[i]))
        const otherBest = others.length ? Math.min(...others.map((i) => vals[i])) : Infinity
        let winners = []
        let losers = []
        if (wolfBest < otherBest) { winners = team; losers = others; winnerSide = 'wolf' }
        else if (otherBest < wolfBest) { winners = others; losers = team; winnerSide = 'field' }
        if (winners.length && losers.length) {
          if (wolfPayout === 'per-winner') {
            winners.forEach((wi) => { nets[ids[wi]] += betVal * losers.length })
            losers.forEach((li) => { nets[ids[li]] -= betVal * winners.length })
          } else {
            const share = (betVal * losers.length) / winners.length
            winners.forEach((wi) => { nets[ids[wi]] += share })
            losers.forEach((li) => { nets[ids[li]] -= betVal })
          }
          exchanged = true
        }
      }
      if (exchanged) { if (winnerSide === 'wolf') wl[wolfId].w += 1; else wl[wolfId].l += 1 }
    } else {
      stopped = true
    }

    holes.push({
      hole, wolfId, wolfIdx, isBls, loneWolf: isLone,
      partnerId: partnerIdx >= 0 ? ids[partnerIdx] : null,
      shuckPartnerId: shuckPickIdx >= 0 ? ids[shuckPickIdx] : null,
      blind: !!pick.blind && isLone, howl: !!pick.howl,
      teamIds, played, betVal, holeValue: baseBet + carried, exchanged, winnerSide,
    })
    if (played) carried = exchanged ? 0 : carried + baseBet
  }

  const rows = players
    .map((p) => ({ id: p.id, name: p.name, net: nets[p.id], w: wl[p.id].w, l: wl[p.id].l }))
    .sort((a, b) => b.net - a.net)
  return { rows, nets, wl, holes, carriedAtEnd: carried }
}
